import React, { useEffect, useState } from 'react';
import { Menu } from './components/Menu';
import SearchPage from './components/SearchPage';
import BrowsePage from './components/BrowsePage';
import UserGroupAdmin from './components-admin/UserGroupAdmin';
import FieldDefinitionsAdmin from './components-admin/FieldDefinitionsAdmin';
import DrawerAdmin from './components-admin/DrawerAdmin';
import DocumentTypeAdmin from './components-admin/DocumentTypeAdmin';
import UserAdmin from './components-admin/UserAdmin';
import LogsAdmin from './components-admin/LogsAdmin';
import Login from './components/Login';
import { initializeMsal, getActiveAccount, getAccessTokenSilent, ensureReauth } from './auth/msal';
// Avoid statically importing @azure/msal-browser types to keep msal library code-splittable
type AccountInfo = any;
import { http } from './api/http';
import { getDrawers } from './api/drawers';

interface PingResponse { message: string; }
interface MeResponse { id: number; userName: string; groups: { id: number; name: string; }[]; }

const LOCAL_KEY_ID = 'selectedDrawerId';
const LOCAL_KEY_NAME = 'selectedDrawerName';
const DEFAULT_ROUTE = 'search';
const ADMIN_ROUTES = [
  'admin-users',
  'admin-groups',
  'admin-drawers-admin',
  'admin-fielddefinitions',
  'admin-documenttypes',
  'admin-logs'
];

const getInitialRoute = () => {
  const hash = window.location.hash || '';
  if (hash.toLowerCase().includes('#search')) return 'search';
  // If querystring has drawerid or any search params -> go to search
  const params = new URLSearchParams(window.location.search || '');
  //if (params.has('drawerid') || Array.from(params.keys()).length > 0) return 'search';
  return window.location.hash.replace('#', '') || DEFAULT_ROUTE;
};

const getInitialDrawerId = () => {
  const params = new URLSearchParams(window.location.search || '');
  const fromUrl = params.get('drawerid');
  if (fromUrl && !isNaN(Number(fromUrl))) return Number(fromUrl);
  const id = localStorage.getItem(LOCAL_KEY_ID);
  return id ? Number(id) : null;
};
const getInitialDrawerName = () => localStorage.getItem(LOCAL_KEY_NAME) || null;

const App: React.FC = () => {
  const [apiResult, setApiResult] = useState<string>('Loading...');
  const [error, setError] = useState<string | null>(null);
  const [selectedDrawerId, setSelectedDrawerId] = useState<number | null>(getInitialDrawerId());
  const [selectedDrawerName, setSelectedDrawerName] = useState<string | null>(getInitialDrawerName());
  const [route, setRoute] = useState<string>(getInitialRoute());
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminDeniedError, setAdminDeniedError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // init msal redirect handling and perform a silent token check before showing UI
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await initializeMsal();
        const existing = getActiveAccount();
        let redirected = false;
        if (existing) {
          // Try to acquire a token silently to confirm we can call the API before showing UI
          try {
            const token = await getAccessTokenSilent();
            if (token && mounted) {
              setAccount(existing);
              setAuthError(null);
            } else {
              // silent failed without throwing - try interactive redirect
              try { await ensureReauth(false); redirected = true; } catch (e) { console.warn('Interactive reauth failed', e); }
            }
          } catch (e) {
            // Silent token acquisition failed; attempt interactive redirect to authenticate automatically
            console.warn('Silent token acquisition failed during startup', e);
            try { await ensureReauth(false); redirected = true; } catch (re) { console.warn('Interactive reauth failed', re); }
          }
        } else {
          // No active account: try to start interactive authentication automatically
          try { await ensureReauth(false); redirected = true; } catch (e) { console.warn('Interactive reauth failed', e); }
        }

        if (!redirected && mounted) setAuthChecked(true);
      } catch (e) {
        console.error('MSAL initialization error', e);
        if (mounted) setAuthChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Acquire ping (only when authenticated) - now via http helper so Authorization header is added
  useEffect(() => {
    if (!account) return;
    http<PingResponse>('/api/ping')
      .then(d => { setApiResult(d.message); setError(null); setAuthError(null); })
      .catch(e => {
        if (e.message.includes('403') || e.message.toLowerCase().includes('not authorized')) {
            setAuthError('Not authorized. Ask an administrator to add your email address to the system.');
        } else if (e.message.startsWith('401')) {
            setError('Unauthorized (401).');
        } else setError(e.message);
      });
  }, [account]);

  // Determine admin by asking API for current user groups ("admin")
  useEffect(() => {
    if (!account) { setIsAdmin(false); return; }
    http<MeResponse>('/api/user/me')
      .then(me => { setIsAdmin(me.groups?.some(g => g.name?.toLowerCase() === 'admin') || false); })
      .catch(() => setIsAdmin(false));
  }, [account]);

  // Ensure navigating away from admin routes if not admin (after login or route change)
  useEffect(() => {
    if (!isAdmin && ADMIN_ROUTES.includes(route)) {
      setAdminDeniedError('You are not authorized for admin functions.');
      window.location.hash = DEFAULT_ROUTE;
      setRoute(DEFAULT_ROUTE);
    }
  }, [route, isAdmin]);

  useEffect(() => {
    const onHashChange = () => {
      const h = window.location.hash || '';
      if (h.toLowerCase().startsWith('#search')) setRoute('search');
      else setRoute(h.replace('#', '') || DEFAULT_ROUTE);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // React to URL search param changes (e.g., direct link with ?drawerid=..)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const fromUrl = params.get('drawerid');
    if (fromUrl && !isNaN(Number(fromUrl))) setSelectedDrawerId(Number(fromUrl));
  }, []);

  const handleDrawerSelect = (id: number, name: string) => {
    setSelectedDrawerId(id); setSelectedDrawerName(name); localStorage.setItem(LOCAL_KEY_ID, String(id)); localStorage.setItem(LOCAL_KEY_NAME, name);
    if (route === 'search' || route === 'browse') { window.location.hash = route; setRoute(route); }
    else if (ADMIN_ROUTES.indexOf(route) !== -1) { window.location.hash = DEFAULT_ROUTE; setRoute(DEFAULT_ROUTE); }
    else { window.location.hash = DEFAULT_ROUTE; setRoute(DEFAULT_ROUTE); }
  };
  useEffect(() => { if (!window.location.hash || window.location.hash === '#') { window.location.hash = DEFAULT_ROUTE; setRoute(DEFAULT_ROUTE); } }, []);
  const handleMenuRoute = (r: string) => { window.location.hash = r; setRoute(r); setAdminDeniedError(null); };

  // If no drawer is selected from URL or local storage, fetch drawers and select the first
  useEffect(() => {
    if (!account) return; // need auth for API calls
    if (selectedDrawerId != null) return; // already have one
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('drawerid')) return; // URL specifies drawer; keep waiting for that effect

    let cancelled = false;
    (async () => {
      try {
        const list = await getDrawers();
        if (!cancelled && list && list.length > 0) {
          const first = list[0]!; // list.length > 0 ensures first exists
          handleDrawerSelect(first.id, first.drawerName);
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [account, selectedDrawerId]);

  // Ensure drawer-badge (name) is populated when drawerId is set by URL or other flows
  useEffect(() => {
    if (!account) return;
    if (!selectedDrawerId) return;
    if (selectedDrawerName && selectedDrawerName.length > 0) return; // already have name

    let cancelled = false;
    (async () => {
      try {
        const list = await getDrawers();
        if (cancelled) return;
        const found = list.find(d => d.id === selectedDrawerId);
        if (found) {
          setSelectedDrawerName(found.drawerName);
          try { localStorage.setItem(LOCAL_KEY_NAME, found.drawerName); } catch {}
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [account, selectedDrawerId, selectedDrawerName]);

  // Wait for auth check to complete before rendering anything
  if (!authChecked) return <div>Loading...</div>;

  // Not logged in yet
  if (!account) return <Login error={authError} />;

  // Logged in but backend rejects user (not in local DB) -> show login with error again
  if (authError) return <Login error={authError} />;

  const userName = account?.name || account?.username || null;
  const userId = (account?.idTokenClaims as any)?.oid || (account as any)?.localAccountId || null;

  return (
    <div className="app-container">
      <Menu selectedDrawerName={selectedDrawerName} onDrawerSelect={handleDrawerSelect} onRouteChange={handleMenuRoute} userName={userName} userId={userId} isAdmin={isAdmin} />
      <main className="app-main">
        {adminDeniedError && (
          <div style={{ background: '#ffe4e4', color: '#b00020', padding: '12px 16px', borderRadius: 6, marginBottom: 16, fontWeight: 500 }}>
            {adminDeniedError} Navigated to default page.
          </div>
        )}
        {route === 'admin-users' ? <UserAdmin />
          : route === 'admin-groups' ? <UserGroupAdmin />
          : route === 'admin-fielddefinitions' ? <FieldDefinitionsAdmin />
          : route === 'admin-drawers-admin' ? <DrawerAdmin />
          : route === 'admin-documenttypes' ? <DocumentTypeAdmin />
          : route === 'admin-logs' ? <LogsAdmin />
          : route === 'search' ? <SearchPage drawerId={selectedDrawerId} />
          : route === 'browse' ? <BrowsePage drawerId={selectedDrawerId} />
          : <>
            <h1>WebReact Frontend</h1>
            <p>Welcome {account.username}</p>
            <section>
              <h2>API Test (/api/ping)</h2>
              {error && <p style={{ color: 'red' }}>Error: {error}</p>}
              {!error && <p>Response: {apiResult}</p>}
            </section>
          </>}
      </main>
    </div>
  );
};

export default App;
